import { useState } from 'react';

import { Paperclip, X } from 'lucide-react';

import {

  Dialog,

  DialogContent,

  DialogDescription,

  DialogFooter,

  DialogHeader,

  DialogTitle,

} from '@/shared/components/ui/dialog';

import { Button } from '@/shared/components/ui/button';

import { Label } from '@/shared/components/ui/label';

import {

  Select,

  SelectContent,

  SelectItem,

  SelectTrigger,

  SelectValue,

} from '@/shared/components/ui/select';

import { Textarea } from '@/shared/components/ui/textarea';

interface Interviewer {

  id: string;

  name: string;

  email?: string;

  designation?: string;

  department?: string;

  role?: string;

  status?: string;

}

interface ScheduleInterviewDialogProps {

  open: boolean;

  onOpenChange: (open: boolean) => void;

  isReschedule?: boolean;

  candidateName: string;

  jobPosition?: string;

  scheduleRound: string;

  scheduleDate: string;

  scheduleTime: string;

  scheduleDuration: string;

  scheduleMode: string;

  scheduleInterviewer: string;

  scheduleMeetingPlatform: string;

  scheduleMeetingLink: string;

  scheduleVenue: string;

  scheduleAddress: string;

  scheduleGoogleMapsLocation: string;

  scheduleInstructions: string;

  interviewers: Interviewer[];

  attachments: File[];

  completedRounds: string[];

  interviewWorkflow: {

    key: string;

    name: string;

    sequence: number;

  }[];

  rescheduleReason: string;

  onRescheduleReasonChange: (value: string) => void;

  scheduleUpdating: boolean;

  onRoundChange: (value: string) => void;

  onDateChange: (value: string) => void;

  onTimeChange: (value: string) => void;

  onDurationChange: (value: string) => void;

  onModeChange: (value: string) => void;

  onInterviewerChange: (value: string) => void;

  onMeetingPlatformChange: (value: string) => void;

  onMeetingLinkChange: (value: string) => void;

  onVenueChange: (value: string) => void;

  onAddressChange: (value: string) => void;

  onGoogleMapsLocationChange: (value: string) => void;

  onInstructionsChange: (value: string) => void;

  onAttachmentsChange: (files: File[]) => void;

  onSave: () => void;

}

interface RejectCandidateDialogProps {

  open: boolean;

  onOpenChange: (open: boolean) => void;

  reason: string;

  statusUpdating: boolean;

  onReasonChange: (value: string) => void;

  onConfirm: () => void;

}

interface BlacklistCandidateDialogProps {

  open: boolean;

  onOpenChange: (open: boolean) => void;

  reason: string;

  statusUpdating: boolean;

  onReasonChange: (value: string) => void;

  onConfirm: () => void;

}

const isValidUrl = (value: string) => {

  const trimmedValue = value.trim();

  if (!trimmedValue) return false;

  try {

    const normalizedValue = /^https?:\/\//i.test(trimmedValue)

      ? trimmedValue

      : `https://${trimmedValue}`;

    const url = new URL(normalizedValue);

    return (

      (url.protocol === 'http:' || url.protocol === 'https:') &&

      Boolean(url.hostname) &&

      url.hostname.includes('.') &&

      !url.hostname.startsWith('.') &&

      !url.hostname.endsWith('.')

    );

  } catch {

    return false;

  }

};

export function ScheduleInterviewDialog({

  open,

  onOpenChange,

  isReschedule = false,

  candidateName,

  jobPosition,

  scheduleRound,

  scheduleDate,

  scheduleTime,

  scheduleDuration,

  scheduleMode,

  scheduleInterviewer,

  scheduleMeetingPlatform,

  scheduleMeetingLink,

  scheduleVenue,

  scheduleAddress,

  scheduleGoogleMapsLocation,

  scheduleInstructions,

  interviewers,

  attachments,

  completedRounds,

  interviewWorkflow,

  rescheduleReason,

  onRescheduleReasonChange,

  scheduleUpdating,

  onRoundChange,

  onDateChange,

  onTimeChange,

  onDurationChange,

  onModeChange,

  onInterviewerChange,

  onMeetingPlatformChange,

  onMeetingLinkChange,

  onVenueChange,

  onAddressChange,

  onGoogleMapsLocationChange,

  onInstructionsChange,

  onAttachmentsChange,

  onSave,

}: ScheduleInterviewDialogProps) {

  const [meetingLinkError, setMeetingLinkError] = useState('');

  const [showScheduleValidation, setShowScheduleValidation] =

    useState(false);

  const isOnline = scheduleMode === 'ONLINE';

  const isOffline = scheduleMode === 'OFFLINE';

  const getTodayDate = () => {

    const now = new Date();

    const year = now.getFullYear();

    const month = String(now.getMonth() + 1).padStart(2, '0');

    const day = String(now.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;

  };

  const getCurrentTime = () => {

    const now = new Date();

    const hours = String(now.getHours()).padStart(2, '0');

    const minutes = String(now.getMinutes()).padStart(2, '0');

    return `${hours}:${minutes}`;

  };

  const todayDate = getTodayDate();

  const minimumTime =

    scheduleDate === todayDate ? getCurrentTime() : undefined;

  const isCompletedRound = completedRounds.includes(scheduleRound);

  const currentWorkflowRound =

    interviewWorkflow.find((round) => round.key === scheduleRound) || null;

  const sortedWorkflow = interviewWorkflow

    .slice()

    .sort((a, b) => a.sequence - b.sequence);

  const nextWorkflowRound =

    sortedWorkflow.find((round) => !completedRounds.includes(round.key)) ||

    null;

  const completedRoundMessage = (() => {

    if (!isCompletedRound) return '';

    const round = interviewWorkflow.find(

      (item) => item.key === scheduleRound,

    );

    return `${round?.name || scheduleRound} has already been completed for this candidate.`;

  })();

  const canSubmit =

    !isCompletedRound &&

    Boolean(scheduleRound) &&

    Boolean(scheduleDate) &&

    Boolean(scheduleTime) &&

    Boolean(scheduleDuration) &&

    Boolean(scheduleInterviewer) &&

    Boolean(scheduleMode) &&

    (!isOnline ||

      (Boolean(scheduleMeetingPlatform) &&

        Boolean(scheduleMeetingLink.trim()))) &&

    (!isOffline ||

      (Boolean(scheduleVenue.trim()) &&

        Boolean(scheduleAddress.trim()) &&

        Boolean(scheduleGoogleMapsLocation.trim()))) &&

    (!isReschedule || Boolean(rescheduleReason.trim()));

  const handleMeetingLinkChange = (value: string) => {

    onMeetingLinkChange(value);

    if (meetingLinkError) {

      setMeetingLinkError('');

    }

  };

  const handleSave = () => {

    if (isCompletedRound) return;

    // Prevent scheduling in the past.

    if (

      scheduleDate === todayDate &&

      scheduleTime &&

      scheduleTime < getCurrentTime()

    ) {

      return;

    }

    // Reschedule reason.

    if (isReschedule && !rescheduleReason.trim()) {

      return;

    }

    // Online interview validation.

    if (isOnline) {

      const meetingLink = scheduleMeetingLink.trim();

      if (!scheduleMeetingPlatform) {

        return;

      }

      if (!meetingLink) {

        setMeetingLinkError(

          'Meeting link is required for online interviews.',

        );

        return;

      }

      if (!isValidUrl(meetingLink)) {

        setMeetingLinkError('Please enter a valid meeting URL.');

        return;

      }

    }

    // Offline interview validation.

    if (isOffline) {

      if (

        !scheduleVenue.trim() ||

        !scheduleAddress.trim() ||

        !scheduleGoogleMapsLocation.trim()

      ) {

        setShowScheduleValidation(true);

        return;

      }

      if (!isValidUrl(scheduleGoogleMapsLocation.trim())) {

        setShowScheduleValidation(true);

        return;

      }

    }

    setMeetingLinkError('');

    onSave();

  };

  return (

    <Dialog open={open} onOpenChange={onOpenChange}>

      <DialogContent className="max-h-[90dvh] w-[calc(100%-1rem)] max-w-lg overflow-y-auto p-4 sm:max-h-[90vh] sm:w-full sm:p-6">

        <DialogHeader className="space-y-2 text-left">

          <DialogTitle className="break-words text-lg sm:text-xl">

            {isReschedule ? 'Reschedule Interview' : 'Schedule Interview'}

          </DialogTitle>

          <DialogDescription className="break-words text-sm">

            {isReschedule

              ? `Reschedule the interview for ${candidateName || 'this candidate'}.`

              : `Schedule the interview for ${candidateName || 'this candidate'}.`}

          </DialogDescription>

        </DialogHeader>

        <div className="min-w-0 space-y-4">

          {/* Candidate */}

          <div className="min-w-0 space-y-2">

            <Label>Candidate</Label>

            <div className="flex min-h-10 min-w-0 items-center break-words rounded-md border border-input bg-muted/40 px-3 py-2 text-sm">

              {candidateName || 'N/A'}

            </div>

          </div>

          {/* Job Position */}

          {jobPosition && (

            <div className="min-w-0 space-y-2">

              <Label>Job Position</Label>

              <div className="flex min-h-10 min-w-0 items-center break-words rounded-md border border-input bg-muted/40 px-3 py-2 text-sm">

                {jobPosition}

              </div>

            </div>

          )}

          {/* Interview Round */}

          <div className="min-w-0 space-y-2">

            <Label>

              Interview Round <span className="text-red-500">*</span>

            </Label>

            <Select

              value={scheduleRound}

              onValueChange={onRoundChange}

              disabled={scheduleUpdating || isReschedule}

            >

              <SelectTrigger className="w-full min-w-0">

                <SelectValue placeholder="Select interview round" />

              </SelectTrigger>

              <SelectContent>

                {sortedWorkflow.map((round) => {

                  const isCompleted = completedRounds.includes(round.key);

                  const isNextRound = nextWorkflowRound?.key === round.key;

                  return (

                    <SelectItem

                      key={round.key}

                      value={round.key}

                      disabled={!isNextRound || isCompleted}

                    >

                      {round.name}

                      {isCompleted && ' (Completed)'}

                    </SelectItem>

                  );

                })}

              </SelectContent>

            </Select>

            {isCompletedRound && completedRoundMessage && (

              <p className="break-words text-sm text-red-500">

                {completedRoundMessage}

              </p>

            )}

          </div>

          {/* Date / Time */}

          <div className="grid min-w-0 grid-cols-1 gap-4 sm:grid-cols-2">

            <div className="min-w-0 space-y-2">

              <Label htmlFor="scheduleDate">

                Interview Date <span className="text-red-500">*</span>

              </Label>

              <input

                id="scheduleDate"

                type="date"

                value={scheduleDate}

                onChange={(event) => onDateChange(event.target.value)}

                min={todayDate}

                disabled={scheduleUpdating || isCompletedRound}

                className="flex h-10 w-full min-w-0 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"

              />

            </div>

            <div className="min-w-0 space-y-2">

              <Label htmlFor="scheduleTime">

                Interview Time <span className="text-red-500">*</span>

              </Label>

              <input

                id="scheduleTime"

                type="time"

                value={scheduleTime}

                min={minimumTime}

                onChange={(event) => {

                  const selectedTime = event.target.value;

                  // If interview is scheduled for today, don't allow a past time.

                  if (

                    scheduleDate === todayDate &&

                    selectedTime < getCurrentTime()

                  ) {

                    return;

                  }

                  onTimeChange(selectedTime);

                }}

                disabled={scheduleUpdating || isCompletedRound}

                className="flex h-10 w-full min-w-0 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"

              />

            </div>

          </div>

          {/* Duration */}

          <div className="min-w-0 space-y-2">

            <Label>

              Duration <span className="text-red-500">*</span>

            </Label>

            <Select

              value={scheduleDuration}

              onValueChange={onDurationChange}

              disabled={scheduleUpdating || isCompletedRound}

            >

              <SelectTrigger className="w-full min-w-0">

                <SelectValue placeholder="Select duration" />

              </SelectTrigger>

              <SelectContent>

                <SelectItem value="30">30 minutes</SelectItem>

                <SelectItem value="45">45 minutes</SelectItem>

                <SelectItem value="60">60 minutes</SelectItem>

                <SelectItem value="90">90 minutes</SelectItem>

                <SelectItem value="120">120 minutes</SelectItem>

              </SelectContent>

            </Select>

          </div>

          {/* Interviewer */}

          <div className="min-w-0 space-y-2">

            <Label>

              Interviewer <span className="text-red-500">*</span>

            </Label>

            <Select

              value={scheduleInterviewer}

              onValueChange={onInterviewerChange}

              disabled={scheduleUpdating || isCompletedRound}

            >

              <SelectTrigger className="w-full min-w-0">

                <SelectValue placeholder="Select interviewer" />

              </SelectTrigger>

              <SelectContent>

                {interviewers.length > 0 ? (

                  interviewers.map((interviewer) => (

                    <SelectItem

                      key={interviewer.id}

                      value={interviewer.id}

                    >

                      <span className="break-words">

                        {interviewer.name}

                        {interviewer.designation

                          ? ` - ${interviewer.designation}`

                          : ''}

                      </span>

                    </SelectItem>

                  ))

                ) : (

                  <SelectItem value="no-interviewers" disabled>

                    No active interviewers available

                  </SelectItem>

                )}

              </SelectContent>

            </Select>

          </div>

          {/* Interview Mode */}

          <div className="min-w-0 space-y-2">

            <Label>

              Interview Mode <span className="text-red-500">*</span>

            </Label>

            <Select

              value={scheduleMode}

              onValueChange={onModeChange}

              disabled={scheduleUpdating || isCompletedRound}

            >

              <SelectTrigger className="w-full min-w-0">

                <SelectValue placeholder="Select interview mode" />

              </SelectTrigger>

              <SelectContent>

                <SelectItem value="ONLINE">Online</SelectItem>

                <SelectItem value="OFFLINE">Offline</SelectItem>

                <SelectItem value="PHONE">Telephonic</SelectItem>

              </SelectContent>

            </Select>

          </div>

          {/* Online */}

          {isOnline && (

            <>

              <div className="min-w-0 space-y-2">

                <Label>

                  Meeting Platform <span className="text-red-500">*</span>

                </Label>

                <Select

                  value={scheduleMeetingPlatform}

                  onValueChange={onMeetingPlatformChange}

                  disabled={scheduleUpdating || isCompletedRound}

                >

                  <SelectTrigger className="w-full min-w-0">

                    <SelectValue placeholder="Select meeting platform" />

                  </SelectTrigger>

                  <SelectContent>

                    <SelectItem value="Google Meet">Google Meet</SelectItem>

                    <SelectItem value="Zoom">Zoom</SelectItem>

                    <SelectItem value="Microsoft Teams">

                      Microsoft Teams

                    </SelectItem>

                    <SelectItem value="Other">Other</SelectItem>

                  </SelectContent>

                </Select>

              </div>

              <div className="min-w-0 space-y-2">

                <Label htmlFor="scheduleMeetingLink">

                  Meeting Link <span className="text-red-500">*</span>

                </Label>

                <input

                  id="scheduleMeetingLink"

                  type="url"

                  value={scheduleMeetingLink}

                  onChange={(event) =>

                    handleMeetingLinkChange(event.target.value)

                  }

                  placeholder="https://meet.google.com/..."

                  aria-invalid={Boolean(meetingLinkError)}

                  disabled={scheduleUpdating || isCompletedRound}

                  className={`flex h-10 w-full min-w-0 rounded-md bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-50 ${

                    meetingLinkError

                      ? 'border border-red-500 focus-visible:ring-red-500'

                      : 'border border-input focus-visible:ring-ring'

                  }`}

                />

                {meetingLinkError && (

                  <p className="break-words text-sm text-red-500">

                    {meetingLinkError}

                  </p>

                )}

              </div>

            </>

          )}

          {/* Offline */}

          {isOffline && (

            <>

              <div className="min-w-0 space-y-2">

                <Label htmlFor="scheduleVenue">

                  Venue <span className="text-red-500">*</span>

                </Label>

                <input

                  id="scheduleVenue"

                  type="text"

                  value={scheduleVenue}

                  onChange={(event) => onVenueChange(event.target.value)}

                  placeholder="Enter interview venue"

                  disabled={scheduleUpdating || isCompletedRound}

                  className="flex h-10 w-full min-w-0 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"

                />

              </div>

              <div className="min-w-0 space-y-2">

                <Label htmlFor="scheduleAddress">

                  Complete Address <span className="text-red-500">*</span>

                </Label>

                <Textarea

                  id="scheduleAddress"

                  value={scheduleAddress}

                  onChange={(event) => onAddressChange(event.target.value)}

                  placeholder="Enter complete interview location address..."

                  disabled={scheduleUpdating || isCompletedRound}

                  className="min-h-[80px] w-full resize-y"

                />

              </div>

              <div className="min-w-0 space-y-2">

                <Label htmlFor="scheduleGoogleMapsLocation">

                  Google Maps Location{' '}

                  <span className="text-red-500">*</span>

                </Label>

                <div className="flex min-w-0 flex-col gap-2 sm:flex-row">

                  <input

                    id="scheduleGoogleMapsLocation"

                    type="url"

                    value={scheduleGoogleMapsLocation}

                    onChange={(event) => {

                      onGoogleMapsLocationChange(event.target.value);

                      if (event.target.value.trim()) {

                        setShowScheduleValidation(false);

                      }

                    }}

                    placeholder="Paste Google Maps location link"

                    disabled={scheduleUpdating || isCompletedRound}

                    className="flex h-10 w-full min-w-0 rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"

                  />

                  <Button

                    type="button"

                    variant="outline"

                    onClick={() => {

                      window.open(

                        'https://www.google.com/maps',

                        '_blank',

                        'noopener,noreferrer',

                      );

                    }}

                    disabled={scheduleUpdating || isCompletedRound}

                    className="w-full shrink-0 sm:w-auto"

                  >

                    Add Google Maps

                  </Button>

                </div>

                {showScheduleValidation && (

                  <p className="break-words text-sm text-red-500">

                    {!scheduleGoogleMapsLocation.trim()

                      ? 'Google Maps Location is required.'

                      : !isValidUrl(scheduleGoogleMapsLocation.trim())

                        ? 'Please enter a valid Google Maps URL.'

                        : ''}

                  </p>

                )}

              </div>

            </>

          )}

          {/* Instructions / Message */}

          <div className="min-w-0 space-y-2">

            <Label htmlFor="scheduleInstructions">Message</Label>

            <Textarea

              id="scheduleInstructions"

              value={scheduleInstructions}

              onChange={(event) => onInstructionsChange(event.target.value)}

              placeholder="Add interview message..."

              disabled={scheduleUpdating || isCompletedRound}

              className="min-h-[80px] w-full resize-y"

            />

          </div>

          {/* Reschedule Reason */}

          {isReschedule && (

            <div className="min-w-0 space-y-2">

              <Label htmlFor="rescheduleReason">

                Reschedule Reason <span className="text-red-500">*</span>

              </Label>

              <Textarea

                id="rescheduleReason"

                value={rescheduleReason}

                onChange={(event) =>

                  onRescheduleReasonChange(event.target.value)

                }

                placeholder="Enter reason for rescheduling the interview..."

                disabled={scheduleUpdating}

                className="min-h-[90px] w-full resize-y"

              />

              {!rescheduleReason.trim() && (

                <p className="text-xs text-red-500">

                  Reschedule reason is required.

                </p>

              )}

            </div>

          )}

          {/* Attachments */}

          <div className="min-w-0 space-y-2">

            <Label>Attachments</Label>

            <label

              htmlFor="interviewAttachments"

              className={`flex cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed border-input px-3 py-4 text-center text-sm transition-colors sm:px-4 ${

                scheduleUpdating || isCompletedRound

                  ? 'cursor-not-allowed opacity-50'

                  : 'hover:bg-muted/50'

              }`}

            >

              <Paperclip className="h-4 w-4 shrink-0" />

              <span>Add attachments</span>

              <input

                id="interviewAttachments"

                type="file"

                multiple

                disabled={scheduleUpdating || isCompletedRound}

                className="hidden"

                accept=".pdf,.doc,.docx,.ppt,.pptx,.jpg,.jpeg,.png,.webp,.mp4,.mov,.avi,.mkv,.webm"

                onChange={(event) => {

                  const selectedFiles = Array.from(

                    event.target.files || [],

                  );

                  const mergedFiles = [...attachments, ...selectedFiles];

                  // Maximum 10 files.

                  if (mergedFiles.length > 10) {

                    return;

                  }

                  onAttachmentsChange(mergedFiles);

                  // Allow selecting the same file again.

                  event.target.value = '';

                }}

              />

            </label>

            {attachments.length > 0 && (

              <div className="min-w-0 space-y-2">

                {attachments.map((file, index) => (

                  <div

                    key={`${file.name}-${index}`}

                    className="flex min-w-0 items-start justify-between gap-2 rounded-md border bg-muted/30 px-3 py-2 sm:items-center"

                  >

                    <div className="min-w-0 flex-1">

                      <p className="break-all text-sm">{file.name}</p>

                      <p className="text-xs text-muted-foreground">

                        {(file.size / 1024 / 1024).toFixed(2)} MB

                      </p>

                    </div>

                    <Button

                      type="button"

                      variant="ghost"

                      size="icon"

                      disabled={scheduleUpdating}

                      onClick={() => {

                        onAttachmentsChange(

                          attachments.filter((_, i) => i !== index),

                        );

                      }}

                      aria-label={`Remove ${file.name}`}

                      className="shrink-0"

                    >

                      <X className="h-4 w-4" />

                    </Button>

                  </div>

                ))}

                <p className="text-xs text-muted-foreground">

                  {attachments.length}/10 files selected. Maximum file size:

                  {' '}100 MB per file.

                </p>

              </div>

            )}

          </div>

        </div>

        {/* Footer */}

        <DialogFooter className="flex-col-reverse gap-2 sm:flex-row sm:justify-end">

          <Button

            type="button"

            variant="outline"

            className="w-full sm:w-auto"

            disabled={scheduleUpdating}

            onClick={() => onOpenChange(false)}

          >

            Cancel

          </Button>

          <Button

            type="button"

            className="w-full sm:w-auto"

            disabled={scheduleUpdating || !canSubmit}

            onClick={handleSave}

          >

            {scheduleUpdating

              ? isReschedule

                ? 'Rescheduling...'

                : 'Scheduling...'

              : isReschedule

                ? 'Reschedule Interview'

                : 'Schedule Interview'}

          </Button>

        </DialogFooter>

      </DialogContent>

    </Dialog>

  );

}

// ======================================================

// Reject Candidate Dialog

// ======================================================

export function RejectCandidateDialog({

  open,

  onOpenChange,

  reason,

  statusUpdating,

  onReasonChange,

  onConfirm,

}: RejectCandidateDialogProps) {

  return (

    <Dialog open={open} onOpenChange={onOpenChange}>

      <DialogContent className="max-h-[90dvh] w-[calc(100%-1rem)] max-w-md overflow-y-auto p-4 sm:max-h-[90vh] sm:w-full sm:p-6">

        <DialogHeader className="space-y-2 text-left">

          <DialogTitle className="text-lg sm:text-xl">

            Reject Candidate

          </DialogTitle>

          <DialogDescription className="text-sm">

            Please provide a reason for rejection.

          </DialogDescription>

        </DialogHeader>

        <div className="min-w-0 space-y-4">

          <div className="min-w-0 space-y-2">

            <Label htmlFor="rejectReason">Rejection Reason</Label>

            <Select onValueChange={onReasonChange}>

              <SelectTrigger id="rejectReason" className="w-full min-w-0">

                <SelectValue placeholder="Select a reason" />

              </SelectTrigger>

              <SelectContent>

                <SelectItem value="experience">

                  Insufficient Experience

                </SelectItem>

                <SelectItem value="skills">Skill Mismatch</SelectItem>

                <SelectItem value="salary">

                  Salary Expectation Too High

                </SelectItem>

                <SelectItem value="fit">Not a Cultural Fit</SelectItem>

                <SelectItem value="ats">Below ATS Threshold</SelectItem>

                <SelectItem value="other">Other</SelectItem>

              </SelectContent>

            </Select>

          </div>

        </div>

        <DialogFooter className="flex-col-reverse gap-2 sm:flex-row sm:justify-end">

          <Button

            type="button"

            variant="outline"

            className="w-full sm:w-auto"

            disabled={statusUpdating}

            onClick={() => onOpenChange(false)}

          >

            Cancel

          </Button>

          <Button

            type="button"

            variant="destructive"

            className="w-full sm:w-auto"

            disabled={statusUpdating || !reason}

            onClick={onConfirm}

          >

            {statusUpdating ? 'Rejecting...' : 'Reject Candidate'}

          </Button>

        </DialogFooter>

      </DialogContent>

    </Dialog>

  );

}

// ======================================================

// Blacklist Candidate Dialog

// ======================================================

export function BlacklistCandidateDialog({

  open,

  onOpenChange,

  reason,

  statusUpdating,

  onReasonChange,

  onConfirm,

}: BlacklistCandidateDialogProps) {

  return (

    <Dialog open={open} onOpenChange={onOpenChange}>

      <DialogContent className="max-h-[90dvh] w-[calc(100%-1rem)] max-w-md overflow-y-auto p-4 sm:max-h-[90vh] sm:w-full sm:p-6">

        <DialogHeader className="space-y-2 text-left">

          <DialogTitle className="text-lg sm:text-xl">

            Blacklist Candidate

          </DialogTitle>

          <DialogDescription className="text-sm">

            This will permanently blacklist this candidate.

          </DialogDescription>

        </DialogHeader>

        <div className="min-w-0 space-y-4">

          <div className="min-w-0 space-y-2">

            <Label htmlFor="blacklistReason">Blacklist Reason</Label>

            <Textarea

              id="blacklistReason"

              value={reason}

              onChange={(event) => onReasonChange(event.target.value)}

              placeholder="Enter blacklist reason..."

              disabled={statusUpdating}

              className="min-h-[100px] w-full resize-y"

            />

          </div>

        </div>

        <DialogFooter className="flex-col-reverse gap-2 sm:flex-row sm:justify-end">

          <Button

            type="button"

            variant="outline"

            className="w-full sm:w-auto"

            disabled={statusUpdating}

            onClick={() => onOpenChange(false)}

          >

            Cancel

          </Button>

          <Button

            type="button"

            variant="destructive"

            className="w-full sm:w-auto"

            disabled={statusUpdating || !reason.trim()}

            onClick={onConfirm}

          >

            {statusUpdating ? 'Processing...' : 'Blacklist'}

          </Button>

        </DialogFooter>

      </DialogContent>

    </Dialog>

  );

}
